/**
 * DictationStrip — proof that the microphone is hearing you.
 *
 * Sits directly above the composer while dictation is open, in the band the
 * attachment previews already occupy. Three things, in the order they matter:
 *
 *  1. A level meter that MOVES. This is the single most reassuring element in
 *     the feature: a bar that responds to your voice answers "is it on?"
 *     without a word of copy, and answers it before any text arrives.
 *  2. The words so far, where the browser can supply them — display only; what
 *     lands in the draft comes back from the transcription tier per pause.
 *  3. The elapsed time and a large stop target, because on a phone the thing
 *     somebody reaches for in a hurry is "make it stop".
 *
 * `role="status"` + `aria-live="polite"` so a screen-reader user is told the
 * microphone is open and when words are being written down — a voice-first
 * control that is silent to assistive tech is the wrong way round.
 */

import React from 'react';
import styled, { keyframes } from 'styled-components';
import { StopSvgIcon } from './components';
import type { DictationLive } from '../../hooks/useDictation';

const BAR_COUNT = 5;

const StripRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  margin: 0 0 6px;
  border: 1px solid var(--brand-line);
  border-radius: 12px;
  background: var(--brand-canvas);
  font-size: 0.78rem;
  color: var(--brand-text-secondary);
  box-sizing: border-box;
  width: 100%;

  /* Phones get a taller row and a bigger stop target — this is the control
     somebody reaches for one-handed while still talking. */
  @media (max-width: 640px) {
    padding: 10px 12px;
    gap: 12px;
  }
`;

const Meter = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  height: 18px;
  flex: 0 0 auto;
`;

const Bar = styled.div<{ $scale: number }>`
  width: 3px;
  height: 18px;
  border-radius: 2px;
  background: var(--brand-gold);
  transform: scaleY(${(p) => Math.max(0.14, Math.min(1, p.$scale))});
  transform-origin: center;
  transition: transform 90ms linear;
`;

const Words = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  color: var(--brand-text);
  font-style: italic;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Hint = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Elapsed = styled.span`
  flex: 0 0 auto;
  font-variant-numeric: tabular-nums;
`;

const pulse = keyframes`
  0%   { opacity: 1; }
  50%  { opacity: 0.5; }
  100% { opacity: 1; }
`;

const Writing = styled.span`
  flex: 0 0 auto;
  animation: ${pulse} 1.4s ease-in-out infinite;
`;

const StopButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 30px;
  height: 30px;
  border-radius: var(--radius-round);
  border: 1px solid var(--brand-line-strong);
  background: var(--brand-surface);
  color: var(--brand-text);
  cursor: pointer;
  padding: 0;

  /* BOTH AXES (2026-09-21). GlobalStyles floors every button at 44 px on both
     axes; this one asked for a 30 px circle and got 44 x 44, because nothing
     here outranked that rule. Doubling the class does — the same escape the
     message meta row uses. Unlike the read-aloud bar it was a circle either
     way, just the wrong size, which is why nobody had noticed. */
  && {
    min-width: 30px;
    min-height: 30px;
  }

  &:hover { border-color: var(--brand-gold); color: var(--brand-gold); }

  svg { width: 12px; height: 12px; stroke: currentColor; stroke-width: 2; fill: none; }

  @media (max-width: 640px) {
    width: 36px;
    height: 36px;
    && {
      min-width: 36px;
      min-height: 36px;
    }
    svg { width: 14px; height: 14px; }
  }
`;

export interface DictationStripProps {
  /**
   * The fast-changing values, read here per animation frame.
   *
   * A REF rather than props on purpose: the level changes about twelve times a
   * second and the partial words nearly as often. As props they would re-render
   * the whole chat shell each time; read here, the cost is confined to this row.
   */
  live: React.MutableRefObject<DictationLive>;
  /** When the microphone opened — this row runs its own clock from it. */
  startedAt: number | null;
  /** True while a finished piece of speech is being written down. */
  transcribing: boolean;
  onStop: () => void;
}

/**
 * Read the live values on every animation frame, and re-render only when
 * something a person can SEE has changed: the level rounded to a tenth (the
 * bars cannot express more) and the words when they differ. The clock ticks
 * from `startedAt`, so it costs one render a second and only of this row.
 */
function useLiveStrip(live: React.MutableRefObject<DictationLive>, startedAt: number | null) {
  const [level, setLevel] = React.useState(0);
  const [interim, setInterim] = React.useState('');
  const [seconds, setSeconds] = React.useState(0);

  React.useEffect(() => {
    if (typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function') return undefined;
    let frame = 0;
    let lastLevel = -1;
    let lastWords = '';
    const step = () => {
      const next = Math.round(Math.min(1, Math.max(0, live.current.level)) * 10) / 10;
      if (next !== lastLevel) {
        lastLevel = next;
        setLevel(next);
      }
      const words = live.current.interim;
      if (words !== lastWords) {
        lastWords = words;
        setInterim(words);
      }
      frame = window.requestAnimationFrame(step);
    };
    frame = window.requestAnimationFrame(step);
    return () => window.cancelAnimationFrame(frame);
  }, [live]);

  React.useEffect(() => {
    if (startedAt === null) {
      setSeconds(0);
      return undefined;
    }
    setSeconds(Math.floor((Date.now() - startedAt) / 1000));
    const timer = setInterval(() => setSeconds(Math.floor((Date.now() - startedAt) / 1000)), 1000);
    return () => clearInterval(timer);
  }, [startedAt]);

  return { level, interim, seconds };
}

function clock(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export const DictationStrip: React.FC<DictationStripProps> = ({
  live,
  startedAt,
  transcribing,
  onStop,
}) => {
  const { level, interim, seconds } = useLiveStrip(live, startedAt);
  return (
  <StripRow role="status" aria-live="polite" data-dictation-strip="">
    <Meter aria-hidden="true">
      {Array.from({ length: BAR_COUNT }, (_, i) => (
        // The middle bars react most, so the meter reads as a voice rather than
        // a progress bar.
        <Bar key={i} $scale={level * (1 - Math.abs(i - (BAR_COUNT - 1) / 2) / BAR_COUNT)} />
      ))}
    </Meter>
    {interim
      ? <Words data-dictation-interim="">{interim}</Words>
      : <Hint>listening — pause when you finish a sentence and it lands in the box</Hint>}
    {transcribing && <Writing>writing it down…</Writing>}
    <Elapsed>{clock(seconds)}</Elapsed>
    <StopButton type="button" onClick={onStop} aria-label="stop dictating" title="stop dictating">
      <StopSvgIcon />
    </StopButton>
  </StripRow>
  );
};

export default DictationStrip;
